import React, { useEffect, useRef } from 'react';
/*
 * Folha do modal de Planejamento: é a FOLHA COMPARTILHADA dos modais de detalhe da
 * Auditoria (o `LiberacaoDetailsModal` faz o mesmo). Criar CSS próprio para repetir
 * `.status-flow-item` e `.badge-change` produziria duas versões do mesmo visual.
 */
import '../../../components/modal/planning-comparison/PlanningComparisonModal.css';
import './AuditoriaCiliaModais.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Diff do complemento (at-05, 16/08/2026) — o análogo direto do "Histórico do Planejamento
 * de Esteira" do SOAC: duas colunas, lista numerada e um badge por linha dizendo o que
 * aconteceu com aquela peça.
 *
 * Por que este evento merece o mesmo tratamento: o XML do Cilia é **cumulativo** (cada
 * complemento é a foto completa do orçamento) e **não marca** o que mudou — o diff é 100%
 * do módulo. Sem esta tela, a única forma de saber o que um complemento acrescentou,
 * removeu ou reduziu era ler `diff_resumo` no banco.
 */

interface Props {
  /** `diff_resumo` como foi gravado no import (snapshot da época). `null` = log legado. */
  diff: Record<string, any> | null;
  /** Peças da versão, com a origem de cada uma (NOVO / HERDADO / QTD_AUMENTADA). */
  itens: Record<string, any>[] | null;
  em: string;
  autor: string | null;
  titulo: string;
  onFechar: () => void;
}

const BADGE: Record<string, { rotulo: string; classe: string }> = {
  NOVO: { rotulo: 'Nova nesta versão', classe: 'font-added' },
  QTD_AUMENTADA: { rotulo: 'Qtd. aumentada', classe: 'font-qtd' },
  HERDADO_ENVIADO: { rotulo: 'Já solicitada', classe: 'font-herdado' },
  HERDADO_RECEBIDO: { rotulo: 'Já recebida', classe: 'font-herdado' },
  REPROCESSADO: { rotulo: 'Reprocessada', classe: 'font-reprocessado' },
};

/*
 * Herdada não é sinônimo de solicitada (18/08/2026). O backend agrupa as situações TRATADAS
 * — ENVIADO e RECEBIDO — sob `origemDiff: 'HERDADO'`, porque para o diff elas são a mesma
 * coisa: não se compra de novo. Mas o rótulo único "Já solicitada" afirmava compra em peça
 * que já havia CHEGADO, e são estados diferentes para quem lê. A situação de origem vem no
 * próprio snapshot (`situacao`), então o texto se resolve aqui, sem tocar no backend.
 *
 * Havia um terceiro caso, `EM_ESTOQUE` ("Atendida pelo estoque"), removido em 20/08/2026
 * junto com a regra de estoque (ADR 0006).
 */
function chaveDoHerdado(situacao: unknown): string {
  if (situacao === 'RECEBIDO') return 'HERDADO_RECEBIDO';
  return 'HERDADO_ENVIADO';
}

function dataHora(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

const AuditoriaDiffComplementoModal: React.FC<Props> = ({ diff, itens, em, autor, titulo, onFechar }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cliqueFora = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onFechar();
    };
    document.addEventListener('mousedown', cliqueFora);
    return () => document.removeEventListener('mousedown', cliqueFora);
  }, [onFechar]);

  const removidos: Record<string, any>[] = diff?.removidos ?? [];
  const reduzidos: Record<string, any>[] = diff?.quantidadeReduzida ?? [];
  const reprocessados: Record<string, any>[] = diff?.reprocessados ?? [];
  const codigosReprocessados = new Set(reprocessados.map((r) => r.codigo ?? r.codigoCilia));

  const semDetalhamento = !diff && !itens;
  const novos = (itens ?? []).filter((i) => i.origemDiff !== 'HERDADO');
  const herdados = (itens ?? []).filter((i) => i.origemDiff === 'HERDADO');

  const linha = (item: Record<string, any>, indice: number, badgeKey: string) => {
    const badge = BADGE[badgeKey] ?? BADGE.NOVO;
    return (
      <div key={`${item.codigo}-${indice}`} className={`status-flow-item item-${badgeKey.toLowerCase()}`}>
        <span className="item-index">{indice}</span>
        <span className="item-label" title={`${item.codigo} — ${item.descricao ?? ''}`}>
          <strong>{item.codigo}</strong> {item.descricao ?? ''}
        </span>
        <span className={`badge-change ${badge.classe}`}>
          {badgeKey === 'QTD_AUMENTADA' && item.quantidadeJaSolicitada != null
            ? `+${Number(item.quantidade ?? 0) - Number(item.quantidadeJaSolicitada)}`
            : badge.rotulo}
        </span>
      </div>
    );
  };

  return (
    <div className="planning-modal-overlay">
      <div className="planning-modal-content" ref={ref} role="dialog" aria-modal="true" aria-label={titulo}>
        <div className="planning-modal-header">
          <h2>{titulo}</h2>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="planning-modal-metadata">
          <div className="metadata-item">Importado por: {autor || 'Sistema'}</div>
          <div className="metadata-item">Data da importação: {dataHora(em)}</div>
        </div>

        <div className="planning-modal-body">
          {semDetalhamento ? (
            <div className="planning-modal-fallback">
              <div className="fallback-title">{titulo}</div>
              <div className="fallback-desc">
                Este registro não contém o detalhamento das peças.
              </div>
              <div className="fallback-note">
                O detalhamento passou a ser guardado em agosto de 2026 — importações anteriores a essa data
                registram apenas os números do complemento.
              </div>
            </div>
          ) : (
            <div className="planning-comparison-grid has-split">
              <div className="planning-column">
                <h3>Saíram do orçamento</h3>
                {removidos.length === 0 && reduzidos.length === 0 ? (
                  <div className="cilia-diff-vazio">Nenhuma peça foi removida nem teve a quantidade reduzida.</div>
                ) : (
                  <div className="status-flow-list">
                    {removidos.map((r, i) => (
                      <div key={`rem-${r.codigo}-${i}`} className="status-flow-item item-removed">
                        <span className="item-index">{i + 1}</span>
                        <span className="item-label" title={`${r.codigo} — ${r.descricao ?? ''}`}>
                          <strong>{r.codigo}</strong> {r.descricao ?? ''}
                        </span>
                        <span className="badge-change font-removed">Removida</span>
                      </div>
                    ))}
                    {reduzidos.map((r, i) => (
                      <div key={`red-${r.codigo}-${i}`} className="status-flow-item item-removed">
                        <span className="item-index">{removidos.length + i + 1}</span>
                        <span className="item-label" title={`${r.codigo} — ${r.descricao ?? ''}`}>
                          <strong>{r.codigo}</strong> {r.descricao ?? ''}
                        </span>
                        <span className="badge-change font-removed">Qtd. reduzida</span>
                      </div>
                    ))}
                  </div>
                )}
                {/*
                  Peça removida que JÁ havia sido solicitada é o caso que o orçamentista
                  precisa ver: o módulo nunca desfaz compra sozinho.
                */}
                {removidos.some((r) => r.situacao === 'ENVIADO' || r.situacao === 'RECEBIDO') && (
                  <div className="cilia-diff-aviso">
                    Há peça removida que já havia sido solicitada — a compra não é desfeita automaticamente.
                  </div>
                )}
              </div>

              <div className="planning-column">
                <h3>Nesta versão</h3>
                <div className="status-flow-list">
                  {novos.map((item, i) =>
                    linha(
                      item,
                      i + 1,
                      codigosReprocessados.has(item.codigo) ? 'REPROCESSADO' : (item.origemDiff ?? 'NOVO'),
                    ),
                  )}
                  {herdados.map((item, i) =>
                    linha(item, novos.length + i + 1, chaveDoHerdado(item.situacao)),
                  )}
                </div>
                {herdados.length > 0 && (
                  <div className="cilia-diff-nota">
                    Peças vindas da versão anterior — <strong>já solicitada</strong> ou{' '}
                    <strong>já recebida</strong> — não são compradas de novo.
                  </div>
                )}
                {/*
                  "Reprocessada" era o único badge sem legenda, e é o mais opaco dos três:
                  não diz contra o que a análise foi feita nem por que a peça voltou.
                */}
                {novos.some((item) => codigosReprocessados.has(item.codigo)) && (
                  <div className="cilia-diff-nota">
                    <strong>Reprocessada</strong>: a peça já estava na versão anterior, mas ainda não tinha
                    virado compra — estava Sob Análise ou Fora do Fluxo. Ela é analisada de novo com os dados
                    desta versão; se agora tiver cobertura, entra na próxima solicitação.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditoriaDiffComplementoModal;
